import { useCallback, useEffect, useState, useRef, type default as React } from 'react';

import { Box } from '@mui/material';
import { useAuthContext } from 'AuthProvider';
import { MasterSearchFrom, FileDownloadData, SearchBox } from 'components/commonparts'
import { DataList, SearchCondition } from 'components/parts/MasterCustomerCenter';
import { useCenter } from 'hooks/useCenter';
import { useCustomer} from 'hooks/useCustomer';
import { useCustomerCenter } from 'hooks/useCustomerCenter';
import { useGridSearch } from 'hooks/useGridSearch';
import { useSearchParamStore } from 'store/searchParamStore';
import { getLocalSearchParam } from 'utils/getLocalSearchParam';

import type { GetMstCustomersCenterCustomerCenterGetRequest, MstCustomerCenter, ResponseResult } from 'api-client';

function MasterCustomerCenter() {
  const { loginUserInfo } = useAuthContext()
  const trCompanyId = loginUserInfo?.companyId;
  const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)
  const searchParamState = useSearchParamStore((state) => state.searchParamState)

   const localSearchParam = getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, loginUserInfo?.companyId)
  const localOrgCustomerId = localSearchParam?.customerCenter.orgCustomerId || undefined

  // 自社のcompanyIdのみ検索条件をローカルストレージから復元
  const initSearchParam: GetMstCustomersCenterCustomerCenterGetRequest = {
    companyId: trCompanyId ?? loginUserInfo!.companyId,
    orgCustomerId: localOrgCustomerId
  }

  const [hasGridEdited, setHasGridEdited] = useState<boolean>(false)
  const [downloadGridRows, setDownloadGridRows] = useState<MstCustomerCenter[]>([])
  const [downloadMode, setDownloadMode] = useState<string>('csv')
  const [downloadColDef, setDownloadColDef] = useState<any[] | undefined>([]);

  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const [gridMessage, setGridMessage] = useState<ResponseResult | undefined>(undefined)
  const [searchParam, setSearchParam] = useState<GetMstCustomersCenterCustomerCenterGetRequest>(initSearchParam)

  const { result, isLoading, customerCenters, fetchCustomerCenter, customerCenterSearchResult } = useCustomerCenter()
  const { customers , fetchCustomer } = useCustomer()
  const { fetchCenterSearch, centerSearch } = useCenter()
  const isFirstRender = useRef(true);
  const [uploadData, setUploadData] = useState<any[]>([])
  const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
  const [updateTrigger, setUpdateTrigger] = useState<boolean | undefined>(undefined);
  const [discardChangeDialogOpen, setDiscardChangeDialogOpen] = useState<boolean>(false)
  
  // Search functionality using custom hook
  const gridSearch = useGridSearch({ autoShowOnCtrlF: true })

  useEffect(() => {
    setResultMessage(customerCenterSearchResult)
  }, [customerCenterSearchResult])
  useEffect(() => {
    setResultMessage(result)
  }, [result])
  useEffect(() => {
    setResultMessage(gridMessage)
  }, [gridMessage])


  const doSearch = useCallback(async (requestParam: GetMstCustomersCenterCustomerCenterGetRequest) => {
    if (hasGridEdited) {
      setDiscardChangeDialogOpen(true)
      return
    }
    try {
      fetchCustomerCenter({
          enabled: false,
          ...requestParam,
        }, true);
        // fetchSupplier({
        //   companyId: requestParam.companyId, 
        // });
        if (requestParam.companyId === loginUserInfo!.companyId && loginUserInfo?.tenantId && loginUserInfo?.companyId) {
          setSearchParamState<GetMstCustomersCenterCustomerCenterGetRequest>(
            loginUserInfo?.tenantId,
            loginUserInfo?.companyId,
            'CustomerCenter',
            {
              ...requestParam,
            }
          );
        }
  
        setSearchParam(requestParam);
        setHasGridEdited(false)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching data:", error);
      } 
  }, [customerCenters]);

  const handleRegister = () => {
    setUpdateTrigger((prev) => !prev);
  }

  const handleUploadData = async (headers: any[], data: any[]) => {
    // エクセルファイルからデータをインポートし、ヘッダーとデータ行に分けて渡される
    const mappedData = data.map((row) => {
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    });
    setUploadData(mappedData); 
  };

  const onReload = () => {
    setSearchTrigger((prev) => !prev);
    setHasGridEdited(false)
  }

  useEffect(() => {
    const firstTimeRender = async () => { 
      if (isFirstRender.current) {
        isFirstRender.current=false
        const itemsPromise = fetchCustomerCenter({
          enabled: false,
          ...searchParam,
        }, true);
        const customerPromise = fetchCustomer({
          enabled: false,
          companyId: searchParam.companyId, 
        }, true);
        const centerPromise = fetchCenterSearch({
          enabled: false,
          companyId: searchParam.companyId, 
        }, true);
        await Promise.all([itemsPromise, customerPromise, centerPromise]);
      }
    }
    firstTimeRender()
  }, [])

  const generateDownloadData = (): MstCustomerCenter[] => {
    if (!customerCenters?.list || customerCenters.list.length === 0) {
      return [{} as MstCustomerCenter]
    }
    const downloadData: MstCustomerCenter[] = [...customerCenters.list]
    return downloadData
  }
  
  const handleFileDownload = () => {
    setDownloadGridRows(generateDownloadData())
  }

  return (
    <Box>
      <MasterSearchFrom
          result={resultMessage}
          onDoUpdate={handleRegister}
          onReload={onReload}
          onImportData={handleUploadData}
          onFileDownLoad={handleFileDownload}
          discardChangeDialogOpen={discardChangeDialogOpen}
          setDownloadMode={setDownloadMode}
          hasDataEdited={hasGridEdited}
          importRequiredFields={["org_customer_id", "org_customer_center_id"]}
          headerJp={["得意先企業ID", "得意先センターID"]}
          lastUpdateDatetime={customerCenters?.lastUpdateDatetime}
          >
          <SearchCondition
            hasDataEdited={hasGridEdited}
            searchParam={initSearchParam}
            onSearch={doSearch}
            setDiscardChangeDialogOpen={setDiscardChangeDialogOpen}
            searchTrigger={searchTrigger}
          />
        </MasterSearchFrom>
      <FileDownloadData fileName="得意先センター" downloadData={downloadGridRows} downloadMode={downloadMode} columnDefs={downloadColDef} />
      <SearchBox
        gridRef={gridSearch.searchBoxProps.gridRef}
        findSearchValue={gridSearch.searchBoxProps.findSearchValue}
        setFindSearchValue={gridSearch.searchBoxProps.setFindSearchValue}
        activeMatchNum={gridSearch.searchBoxProps.activeMatchNum}
        onFindChanged={gridSearch.searchBoxProps.onFindChanged}
        isVisible={gridSearch.searchBoxProps.isVisible}
        onToggle={gridSearch.searchBoxProps.onToggle}
        onShow={gridSearch.searchBoxProps.onShow}
        onHide={gridSearch.searchBoxProps.onHide}
      />
      <DataList
          list={customerCenters}
          customer={customers.list}
          center={centerSearch.list.filter((center) => center.deleteFlg === false)}
          onReload={onReload}
          isLoading={isLoading}
          searchParam={searchParam}
          onUploadData={uploadData}
          updateTrigger={updateTrigger}
          setHasGridEdited={setHasGridEdited}
          setResultMessage={setGridMessage}
          setDownloadColumnDefs={setDownloadColDef}
          gridRef={gridSearch.gridRef}
          findSearchValue={gridSearch.gridSearchProps.findSearchValue}
          onFindChanged={gridSearch.gridSearchProps.onFindChanged}
          isSearchVisible={gridSearch.searchBoxProps.isVisible}
        />

    </Box>
          
  )
}

export default MasterCustomerCenter;
